"use client";

/**
 * Expandable value streams with formula display.
 *
 * Extracted from `compass-assumptions.jsx:129-142` (`.asm-streams`, `.asm-stream`).
 */

import { useState } from "react";
import { FormulaExpression } from "../formula/FormulaExpression";
import { ChevR } from "../lib/icons";

export interface ValueStream {
  id: string;
  kind: "rev" | "cost";
  label: string;
  amount?: string;
  magnitude?: string;
  description?: string;
  formula?: string[];
}

export interface ValueStreamListStream extends ValueStream {
  type: "stream";
}

export interface UnmodelledMetricListItem {
  type: "unmodelled-metric";
  id: string;
  metricId: string;
  label: string;
}

export type ValueStreamListItem =
  | ValueStreamListStream
  | UnmodelledMetricListItem;

export interface ValueStreamsListProps {
  items: ValueStreamListItem[];
  calculated?: boolean;
  label?: string;
}

const NUM_BG: Record<string, string> = {
  rev: "bg-(--bg-sky) text-(--fg-brand)",
  cost: "bg-(--accent-slate-bg) text-(--accent-slate)",
};

export function ValueStreamsList({
  items,
  calculated = true,
  label = "Metrics",
}: ValueStreamsListProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showFx, setShowFx] = useState<Set<string>>(new Set());
  const streamNumberById = new Map<string, number>();
  let nextStreamNumber = 1;
  for (const item of items) {
    if (item.type !== "stream") continue;
    streamNumberById.set(item.id, nextStreamNumber);
    nextStreamNumber += 1;
  }

  function toggleExpand(id: string) {
    setExpanded((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleFx(id: string) {
    setShowFx((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  return (
    <div className="flex-1 min-w-0 bg-(--bg-white) border border-(--border-subtle) rounded-(--radius-container) px-4 py-[10px]">
      <div className="text-[11px] tracking-[.1em] uppercase text-(--fg-muted) font-semibold mb-2">
        {label}
      </div>
      {items.map((item) => {
        if (item.type === "unmodelled-metric") {
          return (
            <div
              key={item.id}
              data-item-type="unmodelled-metric"
              className="flex items-center gap-[10px] border-b border-(--border-subtle) py-[5px] last:border-b-0"
            >
              <span className="w-[42px] shrink-0" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-(--fg-primary)">
                {item.label}
              </span>
              <span className="shrink-0 text-[12px] font-medium text-(--fg-muted)">
                Not modelled
              </span>
            </div>
          );
        }

        const id = item.id;
        return (
          <div
            key={id}
            data-item-type="stream"
            className="border-b border-(--border-subtle) last:border-b-0"
          >
            {/* biome-ignore lint/a11y/useSemanticElements: complex layout prevents using a plain button */}
            <div
              className="flex items-center gap-[10px] py-[5px] cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => toggleExpand(id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleExpand(id);
                }
              }}
            >
              <button
                type="button"
                className={[
                  "bg-transparent border-0 text-(--fg-subtle) cursor-pointer p-0 flex shrink-0 transition-transform duration-300",
                  expanded.has(id) ? "rotate-90" : "",
                ].join(" ")}
                aria-expanded={expanded.has(id)}
              >
                <ChevR size={15} />
              </button>
              <span
                className={[
                  "shrink-0 w-[17px] h-[17px] rounded-[5px] text-[10px] font-bold flex items-center justify-center",
                  NUM_BG[item.kind] ?? "",
                ].join(" ")}
              >
                {streamNumberById.get(item.id)}
              </span>
              <span className="flex-1 min-w-0 text-[13px] font-medium text-(--fg-primary) truncate">
                {item.label}
              </span>
              {item.formula && (
                <button
                  type="button"
                  className="text-[12px] font-bold text-(--fg-muted) bg-transparent border-0 cursor-pointer px-1 hover:text-(--fg-primary)"
                  title="Show formula"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFx(id);
                  }}
                  aria-pressed={showFx.has(id)}
                >
                  ƒx
                </button>
              )}
              <span className="shrink-0 text-right text-[13px] font-bold text-(--fg-primary)">
                {calculated ? item.amount : "—"}
              </span>
            </div>
            {expanded.has(id) && item.description && (
              <div className="pl-[52px] pb-2 text-[12.5px] leading-[1.5] text-(--fg-secondary)">
                {item.description}
              </div>
            )}
            {showFx.has(id) && item.formula && (
              <div className="pl-[52px] pb-2">
                <span className="text-[10px] tracking-[.06em] uppercase text-(--fg-muted) font-semibold mr-2">
                  Formula
                </span>
                <FormulaExpression
                  expression={item.formula}
                  className="text-[12.5px]"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
